import type { FC, ReactNode } from "react";
import { Platform, View, type StyleProp, type ViewStyle } from "react-native";
import { StyleSheet } from "react-native-unistyles";
import "../../theme";
import { TableSectionProvider, useTableLayout } from "./TableContext";

export interface TableSectionProps {
  /** `TableRow`s. */
  readonly children: ReactNode;
  readonly style?: StyleProp<ViewStyle> | undefined;
  readonly testID?: string | undefined;
}

export interface TableHeadProps extends TableSectionProps {
  /**
   * Web only. Reserves room for a vertical scrollbar so the headers stay over
   * their columns when `TableBody` scrolls. Give the scrolling list
   * `scrollbarGutter: "stable"` too, so both always reserve the same width.
   */
  readonly scrollbarGutter?: boolean | undefined;
}

/** Header rows. Cells inside render as column headers. Hidden in the stacked layout. */
const TableHead: FC<TableHeadProps> = ({ children, scrollbarGutter = false, style, testID }) =>
  // Stacked rows have no columns for the headers to label.
  useTableLayout() === "stacked" ? null : (
    <TableSectionProvider value="head">
      <View
        role="rowgroup"
        testID={testID}
        style={[styles.head, scrollbarGutter && Platform.OS === "web" && styles.webScrollbarGutter, style]}
      >
        {children}
      </View>
    </TableSectionProvider>
  );

TableHead.displayName = "TableHead";

/**
 * Data rows. For long tables, render a `FlatList` of `TableRow`s here and give
 * the `TableContainer` a bounded height: the body fills the space left under
 * the header and the list scrolls within it.
 */
const TableBody: FC<TableSectionProps> = ({ children, style, testID }) => (
  <TableSectionProvider value="body">
    <View role="rowgroup" testID={testID} style={[styles.body, style]}>
      {children}
    </View>
  </TableSectionProvider>
);

TableBody.displayName = "TableBody";

/** Summary rows under the data, such as totals or pagination. */
const TableFooter: FC<TableSectionProps> = ({ children, style, testID }) => (
  <TableSectionProvider value="footer">
    <View role="rowgroup" testID={testID} style={style}>
      {children}
    </View>
  </TableSectionProvider>
);

TableFooter.displayName = "TableFooter";

export { TableHead, TableBody, TableFooter };

const styles = StyleSheet.create((theme) => ({
  head: {
    backgroundColor: theme.colors.table.headBackground,
  },
  body: {
    flexGrow: 1,
    flexShrink: 1,
    minHeight: 0,
  },
  // `scrollbar-gutter` only applies to an element that clips its overflow.
  webScrollbarGutter: Object.assign({ overflow: "hidden" } satisfies ViewStyle, {
    scrollbarGutter: "stable",
  }),
}));
